import {
  createSubmitCancelLifecycleContract,
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { accounts } from "./schemas/accounts";
import { assetCategories } from "./schemas/asset-categories";
import { payments } from "./schemas/payments";
import { receipts } from "./schemas/receipts";
import { bankAccounts } from "./schemas/bank-accounts";
import { bankStatementLines } from "./schemas/bank-statement-lines";
import { bankStatementMatches } from "./schemas/bank-statement-matches";
import { budgetLines, budgets } from "./schemas/budgets";
import { cheques } from "./schemas/cheques";
import { costCenters } from "./schemas/cost-centers";
import { currencies } from "./schemas/currencies";
import { depreciationEntries } from "./schemas/depreciation-entries";
import { depreciationSchedules } from "./schemas/depreciation-schedules";
import { journalEntries } from "./schemas/journal-entries";
import { journalEntryItems } from "./schemas/journal-entry-items";
import {
  accountingPurchaseDebitNotes,
  accountingPurchaseCreditNotes,
  accountingPurchaseInvoices,
  accountingSalesCreditNotes,
  accountingSalesDebitNotes,
  accountingSalesInvoices,
} from "./schemas/invoice-documents";
import { whtCertificates } from "./schemas/wht-certificates";
import { fixedAssets } from "./schemas/fixed-assets";
import { dimensionTypes } from "./schemas/dimension-types";
import { dimensionValues } from "./schemas/dimension-values";
import { distributionModelLines } from "./schemas/distribution-model-lines";
import { distributionModels } from "./schemas/distribution-models";
import {
  financialStatementNotes,
  financialStatementNoteVersions,
} from "./schemas/financial-statement-notes";
import { financialStatementPacks } from "./schemas/financial-statement-packs";
import { fiscalYears } from "./schemas/fiscal-years";
import { glEntries } from "./schemas/gl-entries";
import {
  intercompanyLinks,
  intercompanyTransactions,
} from "./schemas/intercompany-links";
import { invoiceItems } from "./schemas/invoice-items";
import { items } from "./schemas/items";
import { jeItemAllocations } from "./schemas/je-item-allocations";
import { paymentAdjustments } from "./schemas/payment-adjustments";
import { paymentAllocations } from "./schemas/payment-allocations";
import { paymentMethods } from "./schemas/payment-methods";
import { periodLocks } from "./schemas/period-locks";
import { priceLists } from "./schemas/price-lists";
import { priceListItems } from "./schemas/price-list-items";
import { recurringTemplates } from "./schemas/recurring-templates";
import { statementLineMappings } from "./schemas/statement-line-mappings";
import { taxFilings } from "./schemas/tax-filings";
import { taxTemplates } from "./schemas/tax-templates";
import { whtTypes } from "./schemas/wht-types";

// ============================================================================
// Accounting module — registered doctypes.
//
// Doctype Admin Epic (#347): #423 migrates the legacy `seed.ts`
// `DEFAULT_NUMBERING_SERIES` per-company seeds for accounting-owned doctypes
// into the registry's `defaultSeries` mechanism. Same shape as #422
// (sales/purchase) — the platform boot loop in
// `apps/tenant-worker/src/middleware/auto-migrate.ts` reads
// `module.registeredDoctypes` across every enabled module and upserts
// platform-default rows into `core_doctype_registry` and
// `core_numbering_series` (as `(module, doctype, company_id=NULL)` rows).
//
// Scope (five active accounting-owned numbering doctypes from the legacy seed
// array). Legacy `accounting/partner` and `accounting/partner_contact` are no
// longer registered; active party masters are `sales/customer` and
// `purchase/supplier`.
//   - `payment`         — outbound payments.
//   - `receipt`         — inbound receipts.
//   - `journal_entry`   — manual + auto journal entries.
//   - `wht_certificate` — withholding-tax certificates (50 ทวิ).
//   - `fixed_asset`     — fixed-asset register.
//
// Per the registry's `tableName` rule, each entry must point at a table
// owned by the accounting module (the validator allows accounting-prefixed
// tables for accounting-owned doctypes; cross-module sharing is also
// allowed, see customer/supplier in sales/purchase).
//
// #644 Phase 0C extends this manifest from numbering-only coverage to a
// full accounting catalog. Extra entries are metadata-only: they classify
// every `accounting_*` table and declare read/search/report policy, but do
// not replace module services or open generic writes for ledgers, child
// tables, junctions, or system controls.
// ============================================================================

export const paymentLifecycle = createSubmitCancelLifecycleContract({
  reversalPolicy: "reverse_gl",
});

export const accountingInvoiceLifecycle = createSubmitCancelLifecycleContract({
  reversalPolicy: "reverse_gl",
});

export const journalEntryLifecycle = createSubmitCancelLifecycleContract({
  reversalPolicy: "reverse_gl",
});

export const whtCertificateLifecycle = createSubmitCancelLifecycleContract({
  docStatusField: null,
  reversalPolicy: "status_only",
  submitDocStatus: null,
  cancelDocStatus: null,
});

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

const JUNCTION_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const LEDGER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const SYSTEM_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

/**
 * Payment (`ใบจ่ายเงิน`) — outbound payments to suppliers
 * (`accounting_payments` rows).
 */
export const paymentDoctype = defineDoctype({
  id: "payment",
  moduleId: "accounting",
  table: payments,
  label: "ใบจ่ายเงิน",
  icon: "CreditCard",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "payment_no",
  displayField: "payment_no",
  imageField: null,
  searchFields: ["payment_no", "status"],
  columnLabels: {
    payment_no: "เลขที่ใบจ่าย",
    branch_id: "สาขา",
    payment_type: "ประเภท",
    party_type: "ประเภทคู่ค้า",
    party_id: "ผู้รับเงิน",
    payment_date: "วันที่",
    payment_method: "วิธีชำระ",
    amount: "จำนวนเงิน",
    reference_no: "เลขอ้างอิง",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "PAY-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: paymentLifecycle,
});

/**
 * Receipt (`ใบเสร็จรับเงิน`) — inbound receipts from customers
 * (`accounting_receipts` rows).
 */
export const receiptDoctype = defineDoctype({
  id: "receipt",
  moduleId: "accounting",
  table: receipts,
  label: "ใบเสร็จรับเงิน",
  icon: "Receipt",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "payment_no",
  displayField: "payment_no",
  imageField: null,
  searchFields: ["payment_no", "status"],
  columnLabels: {
    payment_no: "เลขที่ใบเสร็จ",
    branch_id: "สาขา",
    payment_type: "ประเภท",
    party_type: "ประเภทคู่ค้า",
    party_id: "ผู้จ่ายเงิน",
    payment_date: "วันที่",
    payment_method: "วิธีชำระ",
    amount: "จำนวนเงิน",
    reference_no: "เลขอ้างอิง",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "REC-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: paymentLifecycle,
});

export const bankAccountDoctype = defineDoctype({
  id: "bank_account",
  moduleId: "accounting",
  table: bankAccounts,
  label: "บัญชีธนาคาร",
  icon: "Banknote",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "account_name",
  imageField: null,
  searchFields: ["bank_name", "account_no", "account_name"],
  columnLabels: {
    branch_id: "สาขา",
    account_id: "บัญชี GL",
    bank_name: "ธนาคาร",
    bank_branch: "สาขาธนาคาร",
    account_no: "เลขบัญชี",
    account_name: "ชื่อบัญชี",
    currency_code: "สกุลเงิน",
    is_default: "ค่าเริ่มต้น",
    is_active: "สถานะ",
  },
});

export const chequeDoctype = defineDoctype({
  id: "cheque",
  moduleId: "accounting",
  table: cheques,
  label: "ทะเบียนเช็ค",
  icon: "Receipt",
  features: ["custom_fields", "workflow", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "cheque_no",
  imageField: null,
  searchFields: ["cheque_no", "status", "reference_no"],
  columnLabels: {
    branch_id: "สาขา",
    cheque_type: "ประเภทเช็ค",
    status: "สถานะ",
    cheque_no: "เลขที่เช็ค",
    cheque_date: "วันที่หน้าเช็ค",
    due_date: "วันที่ครบกำหนด",
    payment_id: "เอกสารรับ/จ่าย",
    bank_account_master_id: "บัญชีธนาคาร",
    clearing_account_id: "บัญชีพักเช็ค",
    counterparty_bank_name: "ธนาคารคู่ค้า",
    amount: "จำนวนเงิน",
    reference_no: "เลขอ้างอิง",
  },
});

/**
 * Journal Entry (`สมุดรายวัน`) — manual + auto journal postings.
 *
 * Heavy traffic numbering doctype — used by every auto-posting service
 * (depreciation, fixed-asset disposal, FX revaluation, recurring,
 * year-end close) plus the manual journal entry routes. See
 * `routes/journal-entries.ts` and `services/*.service.ts`.
 */
export const journalEntryDoctype = defineDoctype({
  id: "journal_entry",
  moduleId: "accounting",
  table: journalEntries,
  label: "สมุดรายวัน",
  icon: "BookMarked",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "entry_no",
  displayField: "entry_no",
  imageField: null,
  searchFields: ["entry_no", "status"],
  columnLabels: {
    entry_no: "เลขที่สมุดรายวัน",
    branch_id: "สาขา",
    entry_date: "วันที่",
    entry_type: "ประเภทรายการ",
    reference_module: "โมดูลอ้างอิง",
    reference_doctype: "เอกสารอ้างอิง",
    reference_id: "รหัสอ้างอิง",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "JE-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: journalEntryLifecycle,
});

/**
 * WHT Certificate (`หนังสือรับรองหัก ณ ที่จ่าย`) — 50 ทวิ.
 *
 * Created when withholding tax is deducted from a payment to a supplier.
 * Numbering pair `(MODULES.ACCOUNTING, "wht_certificate")` is referenced
 * from `routes/wht-certificates.ts` (5 call sites covering create / submit
 * / cancel paths).
 */
export const whtCertificateDoctype = defineDoctype({
  id: "wht_certificate",
  moduleId: "accounting",
  table: whtCertificates,
  label: "หนังสือรับรองหัก ณ ที่จ่าย",
  icon: "Receipt",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "certificate_no",
  displayField: "certificate_no",
  imageField: null,
  searchFields: ["certificate_no", "payee_name", "payee_tax_id", "status"],
  columnLabels: {
    certificate_no: "เลขที่หนังสือรับรอง",
    branch_id: "สาขา",
    payee_name: "ชื่อผู้รับเงิน",
    payee_tax_id: "เลขประจำตัวผู้เสียภาษี",
    payment_date: "วันที่จ่ายเงิน",
    income_amount: "จำนวนเงินที่จ่าย",
    wht_rate: "อัตราภาษี",
    wht_amount: "จำนวนภาษีที่หัก",
    net_amount: "จำนวนเงินสุทธิ",
    form_type: "ประเภทแบบ",
    tax_year: "ปีภาษี",
    tax_month: "เดือนภาษี",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "WHT-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: whtCertificateLifecycle,
});

/**
 * Fixed Asset (`สินทรัพย์ถาวร`) — fixed-asset register.
 *
 * Numbering pair `(MODULES.ACCOUNTING, "fixed_asset")` is referenced from
 * `routes/fixed-assets.ts` (8 call sites covering create / dispose /
 * adjust paths). The number lands in `accounting_fixed_assets.asset_code`.
 */
export const fixedAssetDoctype = defineDoctype({
  id: "fixed_asset",
  moduleId: "accounting",
  table: fixedAssets,
  label: "สินทรัพย์ถาวร",
  icon: "Building",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: "asset_code",
  displayField: "asset_name",
  imageField: null,
  searchFields: ["asset_code", "asset_name", "category", "status"],
  columnLabels: {
    asset_code: "รหัสสินทรัพย์",
    asset_name: "ชื่อสินทรัพย์",
    branch_id: "สาขา",
    category: "หมวดสินทรัพย์",
    purchase_date: "วันที่ซื้อ",
    purchase_price: "ราคาซื้อ",
    residual_value: "มูลค่าซาก",
    useful_life_months: "อายุการใช้งาน (เดือน)",
    depreciation_method: "วิธีคิดค่าเสื่อม",
    status: "สถานะ",
    disposal_date: "วันที่จำหน่าย",
    disposal_price: "ราคาจำหน่าย",
    disposal_gain_loss: "กำไร/ขาดทุนจากการจำหน่าย",
  },
  defaultSeries: {
    prefix: "FA-#####",
    resetEvery: "never",
    seriesName: "Standard",
  },
});

const ACCOUNTING_INVOICE_COLUMNS = {
  invoice_no: "เลขที่เอกสาร",
  branch_id: "สาขา",
  party_type: "ประเภทคู่ค้า",
  party_id: "คู่ค้า",
  invoice_date: "วันที่",
  due_date: "วันครบกำหนด",
  subtotal: "ยอดก่อนภาษี",
  tax_amount: "ภาษี",
  total: "ยอดรวม",
  outstanding: "ค้างชำระ",
  status: "สถานะ",
  credit_note_of: "ใบแจ้งหนี้อ้างอิง",
  debit_note_of: "ใบแจ้งหนี้อ้างอิง",
  reason_code: "เหตุผล",
  original_invoice_amount: "ยอดเดิม",
  corrected_amount: "ยอดหลังปรับ",
  difference_amount: "ยอดต่าง",
  remarks: "หมายเหตุ",
};

export const accountingSalesInvoiceDoctype = defineDoctype({
  id: "sales_invoice",
  moduleId: "accounting",
  table: accountingSalesInvoices,
  label: "ใบแจ้งหนี้ขาย",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: ACCOUNTING_INVOICE_COLUMNS,
  defaultSeries: {
    prefix: "SINV-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: accountingInvoiceLifecycle,
});

export const accountingPurchaseInvoiceDoctype = defineDoctype({
  id: "purchase_invoice",
  moduleId: "accounting",
  table: accountingPurchaseInvoices,
  label: "ใบแจ้งหนี้ซื้อ",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: ACCOUNTING_INVOICE_COLUMNS,
  defaultSeries: {
    prefix: "PINV-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: accountingInvoiceLifecycle,
});

export const accountingSalesCreditNoteDoctype = defineDoctype({
  id: "credit_note",
  moduleId: "accounting",
  table: accountingSalesCreditNotes,
  label: "ใบลดหนี้ขาย",
  icon: "FileMinus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: ACCOUNTING_INVOICE_COLUMNS,
  defaultSeries: {
    prefix: "CN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: accountingInvoiceLifecycle,
});

export const accountingPurchaseCreditNoteDoctype = defineDoctype({
  id: "purchase_credit_note",
  moduleId: "accounting",
  table: accountingPurchaseCreditNotes,
  label: "ใบลดหนี้ซื้อ",
  icon: "FileMinus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: ACCOUNTING_INVOICE_COLUMNS,
  defaultSeries: {
    prefix: "CN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: accountingInvoiceLifecycle,
});

export const accountingSalesDebitNoteDoctype = defineDoctype({
  id: "sales_debit_note",
  moduleId: "accounting",
  table: accountingSalesDebitNotes,
  label: "ใบเพิ่มหนี้ขาย",
  icon: "FilePlus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: ACCOUNTING_INVOICE_COLUMNS,
  defaultSeries: {
    prefix: "DN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: accountingInvoiceLifecycle,
});

export const accountingPurchaseDebitNoteDoctype = defineDoctype({
  id: "purchase_debit_note",
  moduleId: "accounting",
  table: accountingPurchaseDebitNotes,
  label: "ใบเพิ่มหนี้ซื้อ",
  icon: "FilePlus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: ACCOUNTING_INVOICE_COLUMNS,
  defaultSeries: {
    prefix: "DN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: accountingInvoiceLifecycle,
});

/**
 * Dimension Type (`มิติบัญชี`) — admin-defined analytic dimension catalog.
 *
 * EPIC #412 Phase 3 standalone dimensions. Registers the dimension *kind*
 * (e.g. "Marketing Campaign", "Region", "Customer Tier") so MCP/AI tools
 * can list and create dimensions through the registry. No numbering series —
 * the user enters a free-form `code`.
 */
export const dimensionTypeDoctype = defineDoctype({
  id: "dimension_type",
  moduleId: "accounting",
  table: dimensionTypes,
  label: "มิติบัญชี",
  icon: "Tags",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name"],
  columnLabels: {
    code: "รหัส",
    name: "ชื่อมิติ",
    description: "คำอธิบาย",
    parent_id: "กลุ่มแม่",
    is_group: "เป็นกลุ่ม",
    is_active: "สถานะ",
  },
});

/**
 * Dimension Value (`ค่ามิติบัญชี`) — admin-defined values within a dimension
 * type (e.g. "Q1-2026 Campaign" under Marketing Campaign).
 *
 * EPIC #412 Phase 3. Used by `accounting_je_item_allocations.dimension_value_id`
 * to tag GL/JE lines with non-doctype analytic values. No numbering series —
 * user-entered `code` is the natural key (unique per company + dimension type).
 */
export const dimensionValueDoctype = defineDoctype({
  id: "dimension_value",
  moduleId: "accounting",
  table: dimensionValues,
  label: "ค่ามิติบัญชี",
  icon: "Tag",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name"],
  columnLabels: {
    code: "รหัส",
    name: "ชื่อค่ามิติ",
    description: "คำอธิบาย",
    dimension_type_id: "ประเภทมิติ",
    parent_id: "กลุ่มแม่",
    is_group: "เป็นกลุ่ม",
    is_active: "สถานะ",
  },
});

/**
 * Item (`สินค้า/บริการ`) — accounting + POS master.
 *
 * Epic #444 promoted Items into the Doctype Admin registry so:
 *   - The new POS columns (`barcode`, `sku`, `is_pos_sellable`) can be
 *     localised via `columnLabels` and configured through Custom Fields.
 *   - Doctype Admin's list-view auto-discovery shows the items grid in
 *     `/admin/doctypes` like every other registered table.
 *
 * Items don't use platform numbering — `item_code` is user-entered (varies
 * by tenant: barcode-derived, internal-SKU, or accounting-style "SRV-001").
 * `numberingField: null` keeps `generateNextNumber` out of the create path.
 */
export const itemDoctype = defineDoctype({
  id: "item",
  moduleId: "accounting",
  table: items,
  label: "สินค้า/บริการ",
  icon: "Package",
  features: ["custom_fields", "permissions", "list_view", "automation"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "item_name",
  imageField: null,
  searchFields: ["item_code", "item_name", "barcode", "sku"],
  columnLabels: {
    item_code: "รหัส",
    item_name: "ชื่อ",
    item_type: "ประเภท",
    barcode: "บาร์โค้ด",
    sku: "SKU",
    is_pos_sellable: "ขายที่ POS",
    income_account_id: "บัญชีรายได้",
    expense_account_id: "บัญชีค่าใช้จ่าย",
    default_rate: "ราคาเริ่มต้น",
    tax_template_id: "แม่แบบภาษี",
    unit_of_measure: "หน่วยนับ",
    is_active: "สถานะ",
  },
});

/**
 * Price List (`ราคาขาย`) — multi-tier pricing master.
 *
 * Epic #444. One row per named tier (Retail / Wholesale / Q2 Promo). Per-
 * row prices live in `accounting_price_list_items` (separate doctype below
 * for admin visibility).
 */
export const priceListDoctype = defineDoctype({
  id: "price_list",
  moduleId: "accounting",
  table: priceLists,
  label: "ราคาขาย",
  icon: "Tag",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
  columnLabels: {
    name: "ชื่อ",
    currency: "สกุลเงิน",
    is_active: "สถานะ",
    valid_from: "เริ่มใช้",
    valid_to: "สิ้นสุด",
  },
});

/**
 * Price List Item (`รายการราคา`) — append-only rate row.
 *
 * Edits to `rate` MUST insert a new row with a fresh `valid_from` (audit
 * trail). The route layer rejects direct rate mutations.
 */
export const priceListItemDoctype = defineDoctype({
  id: "price_list_item",
  moduleId: "accounting",
  table: priceListItems,
  label: "รายการราคา",
  icon: "Receipt",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("price_list", "items", "price_list_id", "valid_from"),
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: [],
  columnLabels: {
    price_list_id: "ราคาขาย",
    item_id: "สินค้า",
    warehouse_id: "คลัง",
    rate: "ราคา",
    min_qty: "จำนวนขั้นต่ำ",
    valid_from: "เริ่มใช้",
    valid_to: "สิ้นสุด",
  },
});

export const taxFilingDoctype = defineDoctype({
  id: "tax_filing",
  moduleId: "accounting",
  table: taxFilings,
  label: "แบบยื่นภาษี",
  icon: "FileSpreadsheet",
  features: ["custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "period_month",
  imageField: null,
  searchFields: ["form_type", "period_month", "filing_reference", "payment_reference"],
  columnLabels: {
    form_type: "แบบภาษี",
    period_month: "งวดภาษี",
    branch_id: "สาขา",
    status: "สถานะ",
    total_base_amount: "ฐานภาษี",
    total_tax_amount: "ภาษีรวม",
    payable_amount: "ยอดต้องชำระ",
    filing_reference: "เลขอ้างอิงการยื่น",
    payment_reference: "เลขอ้างอิงการชำระ",
    period_lock_id: "Period Lock",
  },
});

export const accountDoctype = defineDoctype({
  id: "account",
  moduleId: "accounting",
  table: accounts,
  label: "ผังบัญชี",
  icon: "BookOpen",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "account_name",
  imageField: null,
  searchFields: ["account_number", "account_name", "account_type"],
  columnLabels: {
    account_number: "เลขที่บัญชี",
    account_name: "ชื่อบัญชี",
    account_name_en: "ชื่อบัญชีอังกฤษ",
    account_type: "ประเภทบัญชี",
    parent_id: "บัญชีแม่",
    is_group: "เป็นกลุ่ม",
    balance_direction: "ฝั่งยอดคงเหลือ",
    is_active: "สถานะ",
  },
});

export const assetCategoryDoctype = defineDoctype({
  id: "asset_category",
  moduleId: "accounting",
  table: assetCategories,
  label: "หมวดสินทรัพย์",
  icon: "FolderTree",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "depreciation_method"],
  columnLabels: {
    name: "ชื่อหมวด",
    depreciation_method: "วิธีคิดค่าเสื่อม",
    useful_life_years: "อายุการใช้งาน (ปี)",
    depreciation_rate: "อัตราค่าเสื่อม",
    asset_account_id: "บัญชีสินทรัพย์",
    depreciation_account_id: "บัญชีค่าเสื่อม",
    accumulated_depreciation_account_id: "บัญชีค่าเสื่อมสะสม",
    is_active: "สถานะ",
  },
});

export const bankStatementLineDoctype = defineDoctype({
  id: "bank_statement_line",
  moduleId: "accounting",
  table: bankStatementLines,
  label: "รายการเดินบัญชี",
  icon: "Banknote",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: LEDGER_CATALOG,
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["description", "reference_no", "party_name", "status"],
  columnLabels: {
    branch_id: "สาขา",
    account_id: "บัญชีธนาคาร",
    statement_date: "วันที่รายการ",
    description: "รายละเอียด",
    reference_no: "เลขอ้างอิง",
    party_name: "ชื่อคู่ค้า",
    direction: "ฝั่งเงิน",
    amount: "จำนวนเงิน",
    status: "สถานะ",
    import_batch_id: "ชุดนำเข้า",
  },
});

export const bankStatementMatchDoctype = defineDoctype({
  id: "bank_statement_match",
  moduleId: "accounting",
  table: bankStatementMatches,
  label: "ผลจับคู่รายการเดินบัญชี",
  icon: "GitCompare",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: ["match_type", "target_type", "target_id", "status"],
  columnLabels: {
    statement_line_id: "รายการเดินบัญชี",
    match_type: "ประเภทการจับคู่",
    target_type: "ประเภทเอกสารปลายทาง",
    target_id: "เอกสารปลายทาง",
    matched_amount: "ยอดที่จับคู่",
    variance_amount: "ส่วนต่าง",
    status: "สถานะ",
  },
});

export const budgetDoctype = defineDoctype({
  id: "budget",
  moduleId: "accounting",
  table: budgets,
  label: "งบประมาณ",
  icon: "Target",
  features: ["custom_fields", "workflow", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "status"],
  columnLabels: {
    fiscal_year_id: "ปีภาษี",
    name: "ชื่องบประมาณ",
    period_from: "วันที่เริ่ม",
    period_to: "วันที่สิ้นสุด",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
});

export const budgetLineDoctype = defineDoctype({
  id: "budget_line",
  moduleId: "accounting",
  table: budgetLines,
  label: "รายการงบประมาณ",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("budget", "lines", "budget_id", "month"),
  numberingField: null,
  displayField: "month",
  imageField: null,
  searchFields: ["month"],
  columnLabels: {
    budget_id: "งบประมาณ",
    account_id: "บัญชี",
    cost_center_id: "ศูนย์ต้นทุน",
    month: "เดือน",
    amount: "จำนวนเงิน",
  },
});

export const costCenterDoctype = defineDoctype({
  id: "cost_center",
  moduleId: "accounting",
  table: costCenters,
  label: "ศูนย์ต้นทุน",
  icon: "Network",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name"],
  columnLabels: {
    code: "รหัส",
    name: "ชื่อศูนย์ต้นทุน",
    parent_id: "กลุ่มแม่",
    is_group: "เป็นกลุ่ม",
    is_active: "สถานะ",
  },
});

export const currencyDoctype = defineDoctype({
  id: "currency",
  moduleId: "accounting",
  table: currencies,
  label: "สกุลเงิน",
  icon: "Coins",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "code",
  imageField: null,
  searchFields: ["code", "name", "symbol"],
  columnLabels: {
    code: "รหัสสกุลเงิน",
    name: "ชื่อสกุลเงิน",
    symbol: "สัญลักษณ์",
    exchange_rate: "อัตราแลกเปลี่ยน",
    decimal_places: "ตำแหน่งทศนิยม",
    is_base_currency: "สกุลเงินหลัก",
    is_active: "สถานะ",
  },
});

export const depreciationEntryDoctype = defineDoctype({
  id: "depreciation_entry",
  moduleId: "accounting",
  table: depreciationEntries,
  label: "รายการบันทึกค่าเสื่อม",
  icon: "TrendingDown",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: LEDGER_CATALOG,
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: ["posting_date", "journal_entry_id"],
  columnLabels: {
    fixed_asset_id: "สินทรัพย์",
    posting_date: "วันที่บันทึก",
    amount: "ค่าเสื่อม",
    accumulated_amount: "ค่าเสื่อมสะสม",
    journal_entry_id: "สมุดรายวัน",
  },
});

export const depreciationScheduleDoctype = defineDoctype({
  id: "depreciation_schedule",
  moduleId: "accounting",
  table: depreciationSchedules,
  label: "ตารางค่าเสื่อมราคา",
  icon: "CalendarClock",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("fixed_asset", "depreciation_schedules", "asset_id", "schedule_date"),
  numberingField: null,
  displayField: "schedule_date",
  imageField: null,
  searchFields: ["schedule_date"],
  columnLabels: {
    asset_id: "สินทรัพย์",
    schedule_date: "วันที่คิดค่าเสื่อม",
    depreciation_amount: "ค่าเสื่อม",
    accumulated_depreciation: "ค่าเสื่อมสะสม",
    net_book_value: "มูลค่าตามบัญชี",
    is_booked: "บันทึกแล้ว",
    journal_entry_id: "สมุดรายวัน",
  },
});

export const distributionModelDoctype = defineDoctype({
  id: "distribution_model",
  moduleId: "accounting",
  table: distributionModels,
  label: "กฎการกระจายต้นทุน",
  icon: "Split",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name", "description"],
  columnLabels: {
    code: "รหัส",
    name: "ชื่อกฎ",
    trigger_account_id: "บัญชีเงื่อนไข",
    trigger_account_prefix: "เลขบัญชีขึ้นต้น",
    priority: "ลำดับความสำคัญ",
    description: "คำอธิบาย",
    is_active: "สถานะ",
  },
});

export const distributionModelLineDoctype = defineDoctype({
  id: "distribution_model_line",
  moduleId: "accounting",
  table: distributionModelLines,
  label: "รายการกฎการกระจายต้นทุน",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("distribution_model", "lines", "model_id", "sort_order", "cascade"),
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: [],
  columnLabels: {
    model_id: "กฎการกระจาย",
    reference_module: "โมดูลอ้างอิง",
    reference_doctype: "เอกสารอ้างอิง",
    reference_id: "รหัสอ้างอิง",
    dimension_type_id: "ประเภทมิติ",
    dimension_value_id: "ค่ามิติ",
    percentage: "เปอร์เซ็นต์",
    sort_order: "ลำดับ",
  },
});

export const financialStatementNoteDoctype = defineDoctype({
  id: "financial_statement_note",
  moduleId: "accounting",
  table: financialStatementNotes,
  label: "หมายเหตุประกอบงบการเงิน",
  icon: "StickyNote",
  features: ["custom_fields", "workflow", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "title",
  imageField: null,
  searchFields: ["note_code", "title", "category", "status"],
  columnLabels: {
    branch_id: "สาขา",
    fiscal_year_id: "ปีภาษี",
    pack_version: "เวอร์ชันชุดงบ",
    entity_profile: "รูปแบบกิจการ",
    note_code: "รหัสหมายเหตุ",
    title: "ชื่อหมายเหตุ",
    category: "หมวดหมู่",
    status: "สถานะ",
    narrative_text: "เนื้อหา",
  },
});

export const financialStatementNoteVersionDoctype = defineDoctype({
  id: "financial_statement_note_version",
  moduleId: "accounting",
  table: financialStatementNoteVersions,
  label: "เวอร์ชันหมายเหตุประกอบงบ",
  icon: "History",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("financial_statement_note", "versions", "note_id", "version_number"),
  numberingField: null,
  displayField: "action",
  imageField: null,
  searchFields: ["action"],
  columnLabels: {
    note_id: "หมายเหตุประกอบงบ",
    version_number: "เวอร์ชัน",
    action: "การกระทำ",
    snapshot_json: "ข้อมูล Snapshot",
  },
});

export const financialStatementPackDoctype = defineDoctype({
  id: "financial_statement_pack",
  moduleId: "accounting",
  table: financialStatementPacks,
  label: "ชุดงบการเงิน",
  icon: "FileSpreadsheet",
  features: ["custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "pack_version",
  imageField: null,
  searchFields: ["pack_version", "entity_profile", "status"],
  columnLabels: {
    branch_id: "สาขา",
    fiscal_year_id: "ปีภาษี",
    pack_version: "เวอร์ชัน",
    entity_profile: "รูปแบบกิจการ",
    period_from: "วันที่เริ่ม",
    period_to: "วันที่สิ้นสุด",
    status: "สถานะ",
  },
});

export const fiscalYearDoctype = defineDoctype({
  id: "fiscal_year",
  moduleId: "accounting",
  table: fiscalYears,
  label: "ปีภาษี",
  icon: "CalendarDays",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "year_name",
  imageField: null,
  searchFields: ["year_name"],
  columnLabels: {
    year_name: "ชื่อปีภาษี",
    start_date: "วันที่เริ่ม",
    end_date: "วันที่สิ้นสุด",
    is_closed: "ปิดปีแล้ว",
    closed_at: "วันที่ปิดปี",
    reopened_at: "วันที่เปิดปีใหม่",
  },
});

export const glEntryDoctype = defineDoctype({
  id: "gl_entry",
  moduleId: "accounting",
  table: glEntries,
  label: "บัญชีแยกประเภท",
  icon: "BookOpenCheck",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: LEDGER_CATALOG,
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: ["posting_date", "source_type", "source_id"],
  columnLabels: {
    branch_id: "สาขา",
    account_id: "บัญชี",
    posting_date: "วันที่ลงบัญชี",
    debit: "เดบิต",
    credit: "เครดิต",
    balance: "ยอดคงเหลือ",
    party_type: "ประเภทคู่ค้า",
    party_id: "คู่ค้า",
    source_type: "ประเภทเอกสารต้นทาง",
    source_id: "เอกสารต้นทาง",
    line_no: "ลำดับบรรทัด",
  },
});

export const intercompanyLinkDoctype = defineDoctype({
  id: "intercompany_link",
  moduleId: "accounting",
  table: intercompanyLinks,
  label: "การเชื่อมโยงระหว่างบริษัท",
  icon: "ArrowLeftRight",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["description"],
  columnLabels: {
    from_company_id: "บริษัทต้นทาง",
    to_company_id: "บริษัทปลายทาง",
    from_account_id: "บัญชีต้นทาง",
    to_account_id: "บัญชีปลายทาง",
    description: "คำอธิบาย",
    is_active: "สถานะ",
  },
});

export const intercompanyTransactionDoctype = defineDoctype({
  id: "intercompany_transaction",
  moduleId: "accounting",
  table: intercompanyTransactions,
  label: "รายการระหว่างบริษัท",
  icon: "ArrowLeftRight",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: ["source_doc_type", "source_doc_id", "target_doc_type", "target_doc_id", "status"],
  columnLabels: {
    link_id: "การเชื่อมโยง",
    source_company_id: "บริษัทต้นทาง",
    target_company_id: "บริษัทปลายทาง",
    source_doc_type: "ประเภทเอกสารต้นทาง",
    source_doc_id: "เอกสารต้นทาง",
    target_doc_type: "ประเภทเอกสารปลายทาง",
    target_doc_id: "เอกสารปลายทาง",
    amount: "จำนวนเงิน",
    status: "สถานะ",
  },
});

export const invoiceItemDoctype = defineDoctype({
  id: "invoice_item",
  moduleId: "accounting",
  table: invoiceItems,
  label: "รายการใบแจ้งหนี้",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("invoice", "items", "invoice_id", "sort_order"),
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["description"],
  columnLabels: {
    invoice_id: "ใบแจ้งหนี้",
    item_id: "สินค้า/บริการ",
    description: "รายละเอียด",
    qty: "จำนวน",
    rate: "ราคา",
    amount: "จำนวนเงิน",
    account_id: "บัญชี",
    tax_template_id: "แม่แบบภาษี",
    tax_amount: "ภาษี",
    sort_order: "ลำดับ",
  },
});

export const journalEntryItemDoctype = defineDoctype({
  id: "journal_entry_item",
  moduleId: "accounting",
  table: journalEntryItems,
  label: "รายการสมุดรายวัน",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("journal_entry", "items", "journal_entry_id", "sort_order"),
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["description"],
  columnLabels: {
    journal_entry_id: "สมุดรายวัน",
    account_id: "บัญชี",
    debit: "เดบิต",
    credit: "เครดิต",
    party_type: "ประเภทคู่ค้า",
    party_id: "คู่ค้า",
    description: "รายละเอียด",
    cost_center_id: "ศูนย์ต้นทุน",
    sort_order: "ลำดับ",
  },
});

export const jeItemAllocationDoctype = defineDoctype({
  id: "je_item_allocation",
  moduleId: "accounting",
  table: jeItemAllocations,
  label: "รายการกระจายมิติสมุดรายวัน",
  icon: "Split",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("journal_entry_item", "allocations", "je_item_id", "sort_order"),
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: [],
  columnLabels: {
    je_item_id: "รายการสมุดรายวัน",
    reference_module: "โมดูลอ้างอิง",
    reference_doctype: "เอกสารอ้างอิง",
    reference_id: "รหัสอ้างอิง",
    dimension_type_id: "ประเภทมิติ",
    dimension_value_id: "ค่ามิติ",
    percentage: "เปอร์เซ็นต์",
    amount: "จำนวนเงิน",
    sort_order: "ลำดับ",
  },
});

export const paymentAdjustmentDoctype = defineDoctype({
  id: "payment_adjustment",
  moduleId: "accounting",
  table: paymentAdjustments,
  label: "รายการปรับปรุงการชำระเงิน",
  icon: "ReceiptText",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("payment", "adjustments", "payment_id"),
  numberingField: null,
  displayField: "adjustment_type",
  imageField: null,
  searchFields: ["adjustment_type", "status"],
  columnLabels: {
    payment_id: "การชำระเงิน",
    adjustment_type: "ประเภทปรับปรุง",
    adjustment_date: "วันที่ปรับปรุง",
    amount: "จำนวนเงิน",
    account_id: "บัญชี",
    reason: "เหตุผล",
    status: "สถานะ",
  },
});

export const paymentAllocationDoctype = defineDoctype({
  id: "payment_allocation",
  moduleId: "accounting",
  table: paymentAllocations,
  label: "การตัดชำระใบแจ้งหนี้",
  icon: "GitBranch",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  numberingField: null,
  displayField: "id",
  imageField: null,
  searchFields: ["allocation_type", "status"],
  columnLabels: {
    payment_id: "การชำระเงิน",
    invoice_id: "ใบแจ้งหนี้",
    allocated_amount: "ยอดตัดชำระ",
    allocation_date: "วันที่ตัดชำระ",
    allocation_type: "ประเภท",
    status: "สถานะ",
  },
});

export const paymentMethodDoctype = defineDoctype({
  id: "payment_method",
  moduleId: "accounting",
  table: paymentMethods,
  label: "วิธีชำระเงิน",
  icon: "Wallet",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
  columnLabels: {
    name: "ชื่อวิธีชำระเงิน",
    account_id: "บัญชี",
    requires_reference: "ต้องระบุเลขอ้างอิง",
    is_default: "ค่าเริ่มต้น",
    is_active: "สถานะ",
    is_system: "ระบบสร้าง",
    sort_order: "ลำดับ",
  },
});

export const periodLockDoctype = defineDoctype({
  id: "period_lock",
  moduleId: "accounting",
  table: periodLocks,
  label: "ล็อกงวดบัญชี",
  icon: "Lock",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "period_month",
  imageField: null,
  searchFields: ["period_month"],
  columnLabels: {
    period_month: "งวด",
    locked_at: "วันที่ล็อก",
    locked_by: "ผู้ล็อก",
    reason: "เหตุผล",
  },
});

export const recurringTemplateDoctype = defineDoctype({
  id: "recurring_template",
  moduleId: "accounting",
  table: recurringTemplates,
  label: "รายการซ้ำ",
  icon: "Repeat",
  features: ["custom_fields", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "template_type"],
  columnLabels: {
    branch_id: "สาขา",
    name: "ชื่อรายการซ้ำ",
    description: "คำอธิบาย",
    frequency: "ความถี่",
    next_run_date: "วันที่รันครั้งถัดไป",
    last_run_date: "วันที่รันล่าสุด",
    template_type: "ประเภทแม่แบบ",
    is_active: "สถานะ",
    auto_post: "ยืนยันอัตโนมัติ",
  },
});

export const statementLineMappingDoctype = defineDoctype({
  id: "statement_line_mapping",
  moduleId: "accounting",
  table: statementLineMappings,
  label: "การแมปบรรทัดงบการเงิน",
  icon: "ListTree",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "line_code",
  imageField: null,
  searchFields: ["statement", "line_code", "account_number_prefix", "account_type"],
  columnLabels: {
    pack_version: "เวอร์ชันชุดงบ",
    statement: "งบการเงิน",
    line_code: "รหัสบรรทัด",
    selector_type: "ประเภทตัวเลือก",
    account_id: "บัญชี",
    account_number_prefix: "เลขบัญชีขึ้นต้น",
    account_type: "ประเภทบัญชี",
    exclude_reason: "เหตุผลยกเว้น",
    priority: "ลำดับความสำคัญ",
    is_required: "จำเป็น",
    is_active: "สถานะ",
  },
});

export const taxTemplateDoctype = defineDoctype({
  id: "tax_template",
  moduleId: "accounting",
  table: taxTemplates,
  label: "แม่แบบภาษี",
  icon: "Percent",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "tax_type", "tax_category"],
  columnLabels: {
    name: "ชื่อแม่แบบ",
    rate: "อัตรา",
    tax_type: "ประเภทภาษี",
    tax_category: "หมวดภาษี",
    tax_scope: "ขอบเขตภาษี",
    tax_account_id: "บัญชีภาษี",
    is_default: "ค่าเริ่มต้น",
    is_system: "ระบบสร้าง",
  },
});

export const whtTypeDoctype = defineDoctype({
  id: "wht_type",
  moduleId: "accounting",
  table: whtTypes,
  label: "ประเภทหัก ณ ที่จ่าย",
  icon: "ListChecks",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name", "income_type"],
  columnLabels: {
    code: "รหัส",
    name: "ชื่อ",
    name_en: "ชื่ออังกฤษ",
    income_type: "ประเภทรายได้",
    rate: "อัตราหัก ณ ที่จ่าย",
    wht_account_id: "บัญชีหัก ณ ที่จ่าย",
    is_active: "สถานะ",
    is_system: "ระบบสร้าง",
    sort_order: "ลำดับ",
  },
});

/**
 * Every doctype the accounting module wants registered with the platform.
 * The order here is preserved in the Admin UI through `listDoctypes()` sort.
 */
export const accountingDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  paymentDoctype,
  receiptDoctype,
  journalEntryDoctype,
  whtCertificateDoctype,
  fixedAssetDoctype,
  accountingSalesInvoiceDoctype,
  accountingPurchaseInvoiceDoctype,
  accountingSalesCreditNoteDoctype,
  accountingPurchaseCreditNoteDoctype,
  accountingSalesDebitNoteDoctype,
  accountingPurchaseDebitNoteDoctype,
  dimensionTypeDoctype,
  dimensionValueDoctype,
  // Epic #444 — POS master data.
  itemDoctype,
  priceListDoctype,
  priceListItemDoctype,
  taxFilingDoctype,
  bankAccountDoctype,
  chequeDoctype,
  accountDoctype,
  assetCategoryDoctype,
  bankStatementLineDoctype,
  bankStatementMatchDoctype,
  budgetDoctype,
  budgetLineDoctype,
  costCenterDoctype,
  currencyDoctype,
  depreciationEntryDoctype,
  depreciationScheduleDoctype,
  distributionModelDoctype,
  distributionModelLineDoctype,
  financialStatementNoteDoctype,
  financialStatementNoteVersionDoctype,
  financialStatementPackDoctype,
  fiscalYearDoctype,
  glEntryDoctype,
  intercompanyLinkDoctype,
  intercompanyTransactionDoctype,
  invoiceItemDoctype,
  journalEntryItemDoctype,
  jeItemAllocationDoctype,
  paymentAdjustmentDoctype,
  paymentAllocationDoctype,
  paymentMethodDoctype,
  periodLockDoctype,
  recurringTemplateDoctype,
  statementLineMappingDoctype,
  taxTemplateDoctype,
  whtTypeDoctype,
]);
